import { normalizeFilter } from 'api/notification/normalizers';
import { TObject } from 'utils/types';

import {
  IAllowedUser,
  ICalculatedGraph,
  IDashboard,
  IDashboardFolder,
  IDashboardGraphs,
  IDashboardList,
  IFolderDashboard,
  IGraph,
  IGraphData,
  IGraphRow,
  IStatusGroup,
  IUserSuccessRatesData,
  TCalculatedGraphData,
  TDashboardData,
  TDashboardFolderData,
  TDashboardGraphsData,
  TDashboardListData,
  TGraphData,
  TGraphRowData,
  TStatusGroupData,
} from './types';

const normalizeAllowedUser = (data: TObject): IAllowedUser => {
  return {
    userId: (data?.userId as string) || '',
    userLogin: (data?.userLogin as string) || '',
  };
};

const normalizeFolderDashboard = (data: TObject): IFolderDashboard => {
  return {
    id: (data?.id as number) || null,
    name: (data?.name as string) || null,
  };
};

export const normalizeDashboardFolder = (
  data: TDashboardFolderData,
): IDashboardFolder => {
  return {
    coOwners: (data?.coOwners || []).map(normalizeAllowedUser),
    createdDate: (data?.createdDate as string) || null,
    dashboards: (data?.dashboards || []).map(normalizeFolderDashboard),
    filters: (data?.filters || []).map(normalizeFilter),
    id: (data?.id as number) || 0,
    name: (data?.name as string) || null,
    sharedUsers: (data?.sharedUsers || []).map(normalizeAllowedUser),
    updatedDate: (data?.createdDate as string) || null,
    user: {
      id: (data?.user.id as string) || '',
      login: (data?.user?.login as string) || '',
    },
  };
};

const normalizeGraphRow = (data: TGraphRowData): IGraphRow => {
  return {
    graphIds: data?.graphs?.map((item) => item.id as number) || [],
    rowNum: (data?.rowNum as number) || null,
  };
};

export const normalizeDashboard = (data: TDashboardData): IDashboard => {
  return {
    dynamicFilters: data?.dynamicFilters || [],
    filters: (data?.filters || []).map(normalizeFilter),
    folderId: (data?.folderId as number) || null,
    graphs: (data?.graphs || []).map(normalizeGraphRow),
    id: (data?.id as number) || 0,
    name: (data?.name as string) || null,
  };
};

export const normalizeDashboardList = (data: TDashboardListData): IDashboardList => {
  return {
    result: data?.result.map(normalizeDashboard),
  };
};

export const normalizeStatusGroup = (data: TStatusGroupData): IStatusGroup => {
  return {
    color: (data?.color as string) || null,
    id: (data?.id as number) || 0,
    name: (data?.name as string) || null,
    order: (data?.order as number) || 0,
    statuses: data?.statuses || [],
  };
};

export const normalizeGraph = (data: TGraphData): IGraph => {
  return {
    addProcessedVolume: data?.addProcessedVolume as boolean,
    analyticsGroupParams: data?.analyticsGroupParams || [],
    betweenMetrics: {
      firstInternalTransactionState:
        (data?.betweenMetrics?.firstInternalTransactionState as string) || null,
      firstOperationState: (data?.betweenMetrics?.firstOperationState as string) || null,
      firstOperationType: (data?.betweenMetrics?.firstOperationType as string) || null,
      metrics: data?.betweenMetrics?.metrics || [],
      secondInternalTransactionState:
        (data?.betweenMetrics?.secondInternalTransactionState as string) || null,
      secondOperationState:
        (data?.betweenMetrics?.secondOperationState as string) || null,
      secondOperationType: data?.betweenMetrics?.secondOperationType as null | string,
    },
    createdAsDefault: data?.createdAsDefault as boolean,
    dynamicParentFilters: data?.dynamicParentFilters || [],
    filters: (data?.filters || []).map(normalizeFilter),
    groupingParameter: (data?.groupingParameter as string) || null,
    id: (data?.id as number) || 0,
    name: (data?.name as string) || null,
    numberOfTopResults: (data?.numberOfTopResults as number) || null,
    otherResults: !!data?.otherResults,
    parentFilters: (data?.parentFilters || []).map(normalizeFilter),
    percentages: data?.percentages as boolean,
    pieChartType: (data?.pieChartType as string) || null,
    stackColumns: data?.stackColumns as boolean,
    statusGroups: (data?.statusGroups || [])
      .map(normalizeStatusGroup)
      .sort((a, b) => a.order - b.order),
    type: (data?.type as string) || null,
    viewType: (data?.viewType as string) || null,
  };
};

export const normalizeDashboardGraphs = (
  data: TDashboardGraphsData,
): IDashboardGraphs => {
  return {
    result: data?.result.map(normalizeGraph),
  };
};

const normalizeGraphData = (
  data?: Record<string, TObject[]>,
): Record<string, IGraphData[]> => {
  const graphData = Object.entries(data || {}).map<[string, IGraphData[]]>(
    ([key, value]) => [
      key,
      value.map((item) => ({
        amountSum: (item.amountSum as number) || null,
        status: (item.status as string) || null,
        timestamp: (item.timestamp as string) || null,
        transactionCount: (item.transactionCount as number) || 0,
      })),
    ],
  );

  return Object.fromEntries(graphData);
};

const normalizeUserSuccessRates = (data: TObject): IUserSuccessRatesData => {
  return {
    timestamp: (data?.timestamp as string) || null,
    usersWithoutSuccess: (data?.usersWithoutSuccess as number) || 0,
    usersWithSuccess: (data?.usersWithSuccess as number) || 0,
  };
};

export const normalizeCalculatedGraph = (
  data: TCalculatedGraphData,
): ICalculatedGraph => {
  return {
    addProcessedVolume: data?.addProcessedVolume as boolean,
    analyticsTableGroupParams: (data?.analyticsTableGroupParams as string[]) || [],
    analyticsTableResult: data?.analyticsTableResult || [],
    betweenMetricResult: data?.betweenMetricResult,
    betweenMetricTypes: data?.betweenMetricTypes || [],
    calculatedGraphData: normalizeGraphData(data?.calculatedGraphData),
    groupingParameter: data?.groupingParameter as string,
    numberOfTopResults: data?.numberOfTopResults as number,
    otherResults: data?.otherResults as boolean,
    percentages: data?.percentages as boolean,
    pieChartType: data?.pieChartType as string,
    stackColumns: data?.stackColumns as boolean,
    statusGroups: (data?.statusGroups || []).map(normalizeStatusGroup),
    topParameterAmountResult: data?.topParameterAmountResult,
    topParameterResult: data?.topParameterResult,
    userSuccessRates: data?.userSuccessRates?.map(normalizeUserSuccessRates),
    viewType: data?.viewType as string,
  };
};
